import React from "react";
import { GET_OFFICE_SPEED } from "./graphql";
import { Card, ReportState, useReport, fmt, hrs } from "./ui";

// How fast the office handles what professionals and customers wait on.

// Over this many hours (median) is slow for each area.
const SLOW_HOURS = {
  stock: 24,
  stockFulfil: 72,
  leave: 24,
  disputes: 72,
  verify: 24,
  settle: 48,
  feedbackCall: 24,
  feedbackIssue: 48,
  proIssue: 24,
};

const OfficeSpeedTab = ({ filter }) => {
  const { data, loading, error } = useReport(GET_OFFICE_SPEED, "getOfficeSpeedReport", filter);
  const areaLabel = Object.fromEntries((data?.areas || []).map((a) => [a.key, a.label]));
  const areaKeys = [...new Set((data?.people || []).flatMap((p) => Object.keys(p.areas)))];

  return (
    <ReportState loading={loading} error={error} data={data}>
      {data && (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.areas.map((a) => {
              const slow = a.medianHours !== null && a.medianHours > (SLOW_HOURS[a.key] || 24);
              return (
                <div key={a.key} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900">{a.label}</p>
                      <p className="text-[11px] text-gray-400">
                        {a.from} → {a.to}
                      </p>
                    </div>
                    {a.openNow > 0 && a.ok && <span className="px-1.5 py-0.5 rounded text-[11px] bg-amber-50 text-amber-800 whitespace-nowrap">{fmt(a.openNow)} open</span>}
                  </div>
                  {a.ok ? (
                    <div className="mt-3 flex items-end gap-5">
                      <div>
                        <p className={`text-2xl font-semibold tabular-nums ${slow ? "text-red-600" : "text-gray-900"}`}>{hrs(a.medianHours)}</p>
                        <p className="text-[11px] text-gray-500">typical (median)</p>
                      </div>
                      <div>
                        <p className="text-base font-medium tabular-nums text-gray-700">{hrs(a.p90Hours)}</p>
                        <p className="text-[11px] text-gray-500">slowest 1 in 10</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-base font-medium tabular-nums text-gray-700">{fmt(a.handled)}</p>
                        <p className="text-[11px] text-gray-500">handled</p>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-gray-400">Not available on this database.</p>
                  )}
                </div>
              );
            })}
          </div>

          <Card title="Who in the office handled what" hint="From who each approval, settlement, answer and feedback call is recorded against. Time is the median wait for that person's items.">
            <div className="overflow-x-auto -mx-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Person</th>
                    <th className="px-3 py-2 font-medium">Total</th>
                    {areaKeys.map((k) => (
                      <th key={k} className="px-3 py-2 font-medium whitespace-nowrap">
                        {areaLabel[k] || k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((p) => (
                    <tr key={p.adminId} className="border-b border-gray-50 hover:bg-gray-50 tabular-nums">
                      <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{p.name}</td>
                      <td className="px-3 py-2">{fmt(p.total)}</td>
                      {areaKeys.map((k) => (
                        <td key={k} className="px-3 py-2 whitespace-nowrap">
                          {p.areas[k] ? (
                            <>
                              {fmt(p.areas[k].handled)}
                              {p.areas[k].medianHours !== null && <span className="text-xs text-gray-400"> · {hrs(p.areas[k].medianHours)}</span>}
                            </>
                          ) : (
                            <span className="text-gray-300">·</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!data.people.length && (
                    <tr>
                      <td colSpan={areaKeys.length + 2} className="px-4 py-8 text-center text-gray-400">Nothing recorded against anyone in this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">Booking verification and complaint resolution don't record who did them, so they aren't split by person here; Office Activity has every change by name.</p>
          </Card>
        </>
      )}
    </ReportState>
  );
};

export default OfficeSpeedTab;
